/***************************************************************************************************
 *
 *
 *                 WIP - intended as semi-replacement for with-data-fetching
 *
 *
 ***************************************************************************************************/

/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';
import ReactDOM from 'react-dom';
import {getDisplayName, isClassComponent, compose} from 'recompose';

import {inject} from 'mobx-react';

import {config, SyncServerRouteNames, ConfigClientType} from '../helpers/config-utils';

import axios from 'axios';
import {AxiosResponse, AxiosError} from 'axios';

import {withToastNotifications, ToasterProps} from './with-toast-notifications';

/**************************************** TYPE DEFINITIONS ****************************************/
// export interface GetRESTProps {
//     getSyncServerRouteData?: (routeName: string) => { url: string, reqType: string }
//     config?: ConfigClientType
// }

export interface WithRequestsProps {
    getSyncServerRouteData?: (routeName: string) => {url: string; reqType: string};
    request(
        url: string,
        data: object,
        reqType?: ReqTypes,
        errMsg?: false
    ): Promise<AxiosResponse | AxiosError>;
    request(
        url: string,
        data: object,
        reqType?: ReqTypes,
        errMsg?: string | null
    ): Promise<AxiosResponse>;
}

type ReqTypes = 'get' | 'post' | 'put' | 'options' | 'delete' | 'patch' | 'link';

/********************************************* EXPORT *********************************************/
/**
 * Adds ladda controller and state props to the component.
 *
 * Added "state" props:
 *     config: boolean
 *         Set up so that if this is true, ladda shows up in 1st (or only) possible location.
 *
 * Added "method" props:
 *     getSyncServerRouteData: (routeName: string) => { url: string, reqType: string }
 *         Sets isLoading to true
 */

/**
 * Add function that injects method to grab config options required to make sync server requests.
 * It also automatically injects config data.
 */
const withRequests = (WrappedComponent): any => {
    const Wrapped = compose(
        withToastNotifications,
        inject('config')
    )(
        class extends React.Component<{config: ConfigClientType} & ToasterProps, any> {
            /**
             * Get route based on route name (e.g. verifyPhone, login)
             */
            getSyncServerRouteData = (
                routeName: SyncServerRouteNames,
                dataType: 'both' | 'url' | 'reqType' = 'both'
            ) => {
                const routeData = this.props.config.urls.syncServer[routeName];
                const url = (routeData['url'] || routeData) as string;
                const reqType: string = routeData['reqType'] || 'POST';

                switch (dataType) {
                    case 'reqType':
                        return reqType;
                    case 'url':
                        return url;
                    default:
                    case 'both':
                        return {url, reqType};
                }
            };

            public callApi = (
                url: string,
                reqType: ReqTypes = 'post',
                data: object
            ): Promise<AxiosResponse | AxiosError> =>
                reqType === 'get' ? axios.get(url, {params: data}) : axios[reqType](url, {data});

            public request(
                url: string,
                data: object,
                reqType?: ReqTypes,
                errMsg?: false
            ): Promise<AxiosError>;

            public request(
                url: string,
                data: object,
                reqType?: ReqTypes,
                errMsg?: string | null
            ): Promise<AxiosResponse>;

            public request(
                url: string,
                data: object,
                reqType: ReqTypes = 'post',
                errMsg?: string | false
            ): Promise<AxiosResponse | AxiosError> {
                return this.callApi(url, reqType, data)
                    .catch((err: AxiosError) => {
                        console.error(`${reqType} request to ${url} failed with error:`, err);
                        if (errMsg === false) {
                            return err;
                        } else if (errMsg) {
                            this.props.addNotification('error', errMsg);
                        } else {
                            this.props.addNotification('error', err.message);
                        }
                    })
                    .then((res: AxiosResponse) => {
                        return res;
                    });
            }

            render() {
                return (
                    <WrappedComponent
                        {...this.props}
                        {...this.state}
                        getSyncServerRESTProps={(this as any).getSyncServerRESTProps}
                        callApi={this.callApi}
                        request={this.request}
                    />
                );
            }
        }
    );

    Wrapped.displayName = `withRestRequestProps(${getDisplayName(WrappedComponent)})`;

    return Wrapped;
};

export {withRequests};
